import type { ChangeEvent } from 'react';
import { useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Text, Select, Button, Link, Stack, Loader, tokens } from '@hotjar/hotsauce';
import { useQueryParameter } from '@hotjar/webapp/hooks/useQueryParameter';
import type { GTMAccount, GTMContainer } from '@hotjar/webapp/domains/activation/gtm/model';
import { useHelpHotjarUrl } from '@hotjar/translations';
import { useGTMAccounts } from '../hooks/useGTMAccounts';
import { useGTMContainers } from '../hooks/useGTMContainers';
import { GTMNoAccounts } from './GTMNoAccounts';

const SelectWrapper = styled.div`
  margin-bottom: ${tokens.spacer.x2};
`;
const SelectLabel = styled.label``;

export interface Account {
  name: string;
  accountId: string;
}

export interface Container {
  name: string;
  containerId: string;
  publicId: string;
}

export interface GTMSelectProps {
  onBackClick: () => void;
  onSubmit: (account: GTMAccount, container: GTMContainer) => void;
}

export const GTMSelect = ({ onSubmit, onBackClick }: GTMSelectProps): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const [account, setAccount] = useState<GTMAccount>();
  const [container, setContainer] = useState<GTMContainer>();
  const defaultContainerPublicId = useQueryParameter('gtm_public_id');
  const {
    data: accounts,
    isLoading: areAccountsLoading,
    isFetched: areAccountsFetched,
  } = useGTMAccounts();
  const { data: containers, isLoading: areContainersLoading } = useGTMContainers(account);
  const handleClick = () => {
    if (account && container) {
      onSubmit(account, container);
    }
  };
  const handleAccountChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedAccountId = event.target.value;
    const selectedAccount = accounts?.find(
      (listedAccount: GTMAccount) => listedAccount.accountId === selectedAccountId,
    );
    setAccount(selectedAccount);
  };
  const handleContainerChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedContainerId = event.target.value;
    const selectedContainer = containers?.find(
      (listedContainer: GTMContainer) => listedContainer.containerId === selectedContainerId,
    );
    setContainer(selectedContainer);
  };

  if (areAccountsLoading) return <Loader />;

  if (areAccountsFetched && (!accounts || accounts?.length === 0)) {
    return <GTMNoAccounts onClick={onBackClick} />;
  }

  return (
    <Stack direction="column" spacing="x4">
      <Text textType="body2">{t('activation:gtm-select.title')}</Text>
      <div>
        <SelectWrapper>
          <SelectLabel htmlFor="select_account">
            {t('activation:gtm-select.account-label')}
          </SelectLabel>
          <Select
            aria-label="Account Select"
            id="select_account"
            name="select_account"
            size="m"
            onChange={handleAccountChange}
            isDisabled={areAccountsLoading}
            defaultValue={account?.accountId || ''}
          >
            <option value="" disabled>
              {t('activation:gtm-select.account-default-option')}
            </option>
            {accounts?.map((listedAccount: GTMAccount) => (
              <option key={listedAccount.accountId} value={listedAccount.accountId}>
                {listedAccount.name}
              </option>
            ))}
          </Select>
        </SelectWrapper>
        <SelectWrapper>
          <SelectLabel htmlFor="select_container">
            {t('activation:gtm-select.container-label')}
          </SelectLabel>
          <Select
            aria-label="Container Select"
            isDisabled={!account || areContainersLoading}
            id="select_container"
            name="select_container"
            size="m"
            onChange={handleContainerChange}
            defaultValue={container?.containerId || ''}
          >
            <option value="" disabled>
              {t('activation:gtm-select.container-default-option')}
            </option>
            {containers?.map((listedContainers: GTMContainer) => (
              <option
                key={listedContainers.containerId}
                value={listedContainers.containerId}
                selected={defaultContainerPublicId === listedContainers.publicId}
              >
                {listedContainers.name}
              </option>
            ))}
          </Select>
        </SelectWrapper>
      </div>
      <div>
        <Button isDisabled={!account || !container} onClick={handleClick}>
          {t('activation:gtm-select.publish-cta')}
        </Button>
      </div>
      <div>
        <Link
          kind="standalone"
          isExternal
          href={`${helpHotjarUrl}/articles/115009499708-Google-Tag-Manager`}
        >
          {t('activation:gtm-select.help-cta')}
        </Link>
      </div>
    </Stack>
  );
};
